/**
 * Hospital Auth Service — Resolves hospital identity from auth user.
 * Pattern mirrors vendorService.ts
 */
import { supabase } from '@/integrations/supabase/client';

export type HospitalPortalRole = 'hospital_master' | 'hospital_partner';
export type HospitalAccessScope = 'all_hospitals' | 'single_hospital';

export interface HospitalProfile {
  userId: string;
  hospitalId: string;
  hospitalName: string;
  fullName: string;
  status: string;
  role: HospitalPortalRole;
  accessScope: HospitalAccessScope;
  canAccessAllHospitals: boolean;
  /** True when user has hospital_master role — sees all data across all hospitals */
  isHospitalMaster: boolean;
}

type HospitalAccountRow = {
  id: string;
  user_id: string;
  hospital_id: string | null;
  full_name: string | null;
  status: string;
  role?: string | null;
  access_scope?: string | null;
  hospitals?: {
    id: string;
    name: string;
  } | null;
};

const LEGACY_ROLE_MAP: Record<string, HospitalPortalRole> = {
  hospital: 'hospital_partner',
  hospital_partner: 'hospital_partner',
  hospital_master: 'hospital_master',
};

function normalizeHospitalRole(value: string | null | undefined): HospitalPortalRole | null {
  if (!value) return null;
  return LEGACY_ROLE_MAP[value] ?? null;
}

function normalizeAccessScope(value: string | null | undefined, role: HospitalPortalRole | null): HospitalAccessScope | null {
  if (value === 'all_hospitals' || value === 'single_hospital') return value;
  if (role === 'hospital_master') return 'all_hospitals';
  if (role === 'hospital_partner') return 'single_hospital';
  return null;
}

/**
 * Resolve the hospital profile for the current authenticated user.
 * Source of truth: hospital_accounts.role + hospital_accounts.access_scope.
 * Legacy fallbacks remain for older role setups.
 */
export async function resolveHospitalProfile(userId: string): Promise<HospitalProfile | null> {
  console.log('[HospitalAuth] Resolving identity for:', userId);

  const { data: { user: authUser } } = await supabase.auth.getUser();

  const { data: account, error } = await supabase
    .from('hospital_accounts' as any)
    .select('id, user_id, hospital_id, full_name, status, role, access_scope, hospitals(id, name)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (!account) {
    // hospital_master without a hospital_accounts row (e.g. super admin) → all hospitals.
    const { data: isMaster } = await supabase.rpc('has_role', { _user_id: userId, _role: 'hospital_master' });
    if (isMaster === true) {
      return {
        userId,
        hospitalId: 'all',
        hospitalName: 'All Hospitals',
        fullName: (authUser?.user_metadata?.full_name as string | undefined) || 'Hospital Master',
        status: 'active',
        role: 'hospital_master',
        accessScope: 'all_hospitals',
        canAccessAllHospitals: true,
        isHospitalMaster: true,
      };
    }
  }

  if (error || !account) {
    console.log('[HospitalAuth] ❌ No active hospital account found', error?.message);
    return null;
  }

  const metadataRole = normalizeHospitalRole(
    (authUser?.app_metadata?.role as string | undefined)
      ?? (authUser?.user_metadata?.role as string | undefined)
  );
  const metadataScope = (authUser?.app_metadata?.access_scope as string | undefined)
    ?? (authUser?.user_metadata?.access_scope as string | undefined);

  const accountRow = account as HospitalAccountRow;
  const role = normalizeHospitalRole(accountRow.role ?? metadataRole);
  const accessScope = normalizeAccessScope(accountRow.access_scope ?? metadataScope, role);

  if (!role || !accessScope) {
    console.log('[HospitalAuth] ❌ Invalid role/access_scope on hospital account', {
      role: accountRow.role,
      access_scope: accountRow.access_scope,
    });
    return null;
  }

  const hospital = accountRow.hospitals as HospitalAccountRow['hospitals'];
  const canAccessAllHospitals = role === 'hospital_master' && accessScope === 'all_hospitals';

  if (!canAccessAllHospitals && !accountRow.hospital_id) {
    console.log('[HospitalAuth] ❌ Partner account missing hospital_id');
    return null;
  }

  console.log('[HospitalAuth] ✅ Access resolved:', {
    role,
    accessScope,
    hospital: hospital?.name,
  });

  return {
    userId,
    hospitalId: canAccessAllHospitals ? 'all' : (accountRow.hospital_id ?? 'unknown'),
    hospitalName: canAccessAllHospitals ? 'All Hospitals' : (hospital?.name ?? 'Hospital'),
    fullName: accountRow.full_name || (canAccessAllHospitals ? 'Hospital Master' : 'Hospital Partner'),
    status: accountRow.status,
    role,
    accessScope,
    canAccessAllHospitals,
    isHospitalMaster: canAccessAllHospitals,
  };
}

/**
 * Update last_login_at for hospital account
 */
export async function updateHospitalLastLogin(userId: string): Promise<void> {
  const { error } = await supabase
    .from('hospital_accounts')
    .update({ last_login_at: new Date().toISOString() })
    .eq('user_id', userId);

  if (error) {
    console.warn('[HospitalAuth] Failed to update last_login_at:', error.message);
  }
}
